import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

const getAxisNameKey = (widgetId: string): string | null =>
  widgetId ? `axisNames${widgetId}` : null;

type AxisNameMap = Record<string, string>;

interface AxisNameState {
  axisNames: Record<string, AxisNameMap>;
}

interface AxisNameActions {
  loadFromStorage: (widgetId: string) => void;
  setAxisName: (widgetId: string, key: string, value: string | null) => void;
  resetToNull: (widgetId: string) => void;
  resetKeys: (widgetId: string, keys: string[]) => void;
  getAxisNames: (widgetId: string) => AxisNameMap;
  reset: () => void;
}

const initialState: AxisNameState = {
  axisNames: {}
};

export const useAxisNameStore = create<AxisNameState & AxisNameActions>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    loadFromStorage: (widgetId) => {
      const storageKey = getAxisNameKey(widgetId);
      if (!storageKey) return;
      try {
        const raw = localStorage.getItem(storageKey);
        const parsed: AxisNameMap = raw ? JSON.parse(raw) : {};
        set((state) => ({
          axisNames: { ...state.axisNames, [widgetId]: parsed }
        }));
      } catch {
        set((state) => ({
          axisNames: { ...state.axisNames, [widgetId]: {} }
        }));
      }
    },

    setAxisName: (widgetId, key, value) => {
      const storageKey = getAxisNameKey(widgetId);
      if (!storageKey) return;
      set((state) => {
        const current = state.axisNames[widgetId] ?? {};
        let updated: AxisNameMap;
        if (value === null) {
          updated = { ...current };
          delete updated[key];
        } else {
          updated = { ...current, [key]: value };
        }
        try {
          localStorage.setItem(storageKey, JSON.stringify(updated));
        } catch {
          // storage unavailable — continue with in-memory state only
        }
        return { axisNames: { ...state.axisNames, [widgetId]: updated } };
      });
    },

    resetToNull: (widgetId) => {
      const storageKey = getAxisNameKey(widgetId);
      if (storageKey) {
        try {
          localStorage.removeItem(storageKey);
        } catch {
          // storage unavailable
        }
      }
      set((state) => ({
        axisNames: { ...state.axisNames, [widgetId]: {} }
      }));
    },

    resetKeys: (widgetId, keys) => {
      const storageKey = getAxisNameKey(widgetId);
      set((state) => {
        const current = state.axisNames[widgetId] ?? {};
        const keysToDelete = keys.filter((k) => k in current);
        if (keysToDelete.length === 0) return state; // nothing to remove; don't touch localStorage
        const updated = { ...current };
        keysToDelete.forEach((k) => delete updated[k]);
        if (storageKey) {
          try {
            localStorage.setItem(storageKey, JSON.stringify(updated));
          } catch {
            // storage unavailable
          }
        }
        return { axisNames: { ...state.axisNames, [widgetId]: updated } };
      });
    },

    getAxisNames: (widgetId) => {
      return get().axisNames[widgetId] ?? {};
    },

    reset: () => {
      Object.keys(get().axisNames).forEach((widgetId) => {
        const storageKey = getAxisNameKey(widgetId);
        if (storageKey) {
          try {
            localStorage.removeItem(storageKey);
          } catch {
            // storage unavailable
          }
        }
      });
      set(initialState);
    }
  }))
);
